import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../store';

// 型定義
export interface StageInfo {
  stage_number: number;
  stage_name: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  output?: any;
}

export interface DocumentSession {
  id: number;
  prompt: string;
  status: string;
  current_stage: number;
  progress_percentage: number;
  stage_name: string;
  status_label: string;
  created_at: string;
  updated_at: string;
  final_output?: string;
  error_message?: string;
  error_stage?: number;
  stages?: StageInfo[];
  options?: any;
}

export interface DocumentMessage {
  id: string;
  type: 'user' | 'system' | 'stage_progress' | 'stage_output';
  content: string;
  timestamp: string;
  stage_info?: {
    stage_number: number;
    stage_name: string;
    status: 'pending' | 'running' | 'completed' | 'failed';
    progress?: number;
  };
  code_block?: {
    language: 'yaml' | 'html' | 'css' | 'json' | 'text';
    content: string;
    title?: string;
  };
}

interface DocumentState {
  sessions: DocumentSession[];
  currentSession: DocumentSession | null;
  messages: DocumentMessage[];
  isCreating: boolean;
  isLoadingSessions: boolean;
  isLoadingMessages: boolean;
  error: string | null;
  pagination: {
    current_page: number;
    last_page: number;
    total: number;
  } | null;
}

// 初期状態
const initialState: DocumentState = {
  sessions: [],
  currentSession: null,
  messages: [],
  isCreating: false,
  isLoadingSessions: false,
  isLoadingMessages: false,
  error: null,
  pagination: null
};

// 非同期アクション

// セッション一覧取得
export const fetchDocumentSessions = createAsyncThunk(
  'document/fetchSessions',
  async (page: number = 1, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/document-sessions?page=${page}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('セッション一覧の取得に失敗しました');
      }

      return await response.json();
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error');
    }
  }
);

// セッション詳細取得
export const fetchDocumentSession = createAsyncThunk(
  'document/fetchSession',
  async (sessionId: number, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/document-sessions/${sessionId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('セッション詳細の取得に失敗しました');
      }

      return await response.json();
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error');
    }
  }
);

// セッション作成
export const createDocumentSession = createAsyncThunk(
  'document/createSession',
  async ({ prompt, options }: { prompt: string; options?: any }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/document-sessions', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          prompt,
          options
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || '資料作成の開始に失敗しました');
      }

      return await response.json();
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error');
    }
  }
);

// セッション削除
export const deleteDocumentSession = createAsyncThunk(
  'document/deleteSession',
  async (sessionId: number, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/document-sessions/${sessionId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('セッションの削除に失敗しました');
      }

      return sessionId;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error');
    }
  }
);

// メッセージ履歴取得
export const fetchSessionMessages = createAsyncThunk(
  'document/fetchMessages',
  async (sessionId: number, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/document-sessions/${sessionId}/messages`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('メッセージ履歴の取得に失敗しました');
      }

      const data = await response.json();
      return data.messages;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error');
    }
  }
);

// スライス定義
const documentSlice = createSlice({
  name: 'document',
  initialState,
  reducers: {
    // エラーをクリア
    clearError: (state) => {
      state.error = null;
    },
    
    // 現在のセッションをクリア
    clearCurrentSession: (state) => {
      state.currentSession = null;
      state.messages = [];
    },
    
    // メッセージを追加
    addMessage: (state, action: PayloadAction<DocumentMessage>) => {
      state.messages.push(action.payload);
    },
    
    // メッセージをクリア
    clearMessages: (state) => {
      state.messages = [];
    },
    
    // セッション状態を更新
    updateSessionStatus: (state, action: PayloadAction<Partial<DocumentSession>>) => {
      if (state.currentSession) {
        state.currentSession = { ...state.currentSession, ...action.payload };
      }
      
      // sessions配列内の対応するセッションも更新
      const sessionIndex = state.sessions.findIndex(s => s.id === state.currentSession?.id);
      if (sessionIndex !== -1) {
        state.sessions[sessionIndex] = { ...state.sessions[sessionIndex], ...action.payload };
      }
    }
  },
  extraReducers: (builder) => {
    // セッション一覧取得
    builder
      .addCase(fetchDocumentSessions.pending, (state) => {
        state.isLoadingSessions = true;
        state.error = null;
      })
      .addCase(fetchDocumentSessions.fulfilled, (state, action) => {
        state.isLoadingSessions = false;
        state.sessions = action.payload.sessions;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchDocumentSessions.rejected, (state, action) => {
        state.isLoadingSessions = false;
        state.error = action.payload as string;
      });

    // セッション詳細取得
    builder
      .addCase(fetchDocumentSession.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchDocumentSession.fulfilled, (state, action) => {
        state.currentSession = action.payload;
      })
      .addCase(fetchDocumentSession.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // セッション作成
    builder
      .addCase(createDocumentSession.pending, (state) => {
        state.isCreating = true;
        state.error = null;
      })
      .addCase(createDocumentSession.fulfilled, (state, action) => {
        state.isCreating = false;
        state.currentSession = action.payload;
        state.sessions.unshift(action.payload); // 新しいセッションを先頭に追加
      })
      .addCase(createDocumentSession.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.payload as string;
      });

    // セッション削除
    builder
      .addCase(deleteDocumentSession.fulfilled, (state, action) => {
        state.sessions = state.sessions.filter(session => session.id !== action.payload);
        if (state.currentSession?.id === action.payload) {
          state.currentSession = null;
          state.messages = [];
        }
      })
      .addCase(deleteDocumentSession.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // メッセージ履歴取得
    builder
      .addCase(fetchSessionMessages.pending, (state) => {
        state.isLoadingMessages = true;
        state.error = null;
      })
      .addCase(fetchSessionMessages.fulfilled, (state, action) => {
        state.isLoadingMessages = false;
        state.messages = action.payload;
      })
      .addCase(fetchSessionMessages.rejected, (state, action) => {
        state.isLoadingMessages = false;
        state.error = action.payload as string;
      });
  }
});

// アクション
export const {
  clearError,
  clearCurrentSession,
  addMessage,
  clearMessages,
  updateSessionStatus
} = documentSlice.actions;

// セレクター
export const selectDocumentSessions = (state: RootState) => state.document.sessions;
export const selectCurrentSession = (state: RootState) => state.document.currentSession;
export const selectDocumentMessages = (state: RootState) => state.document.messages;
export const selectIsCreating = (state: RootState) => state.document.isCreating;
export const selectIsLoadingSessions = (state: RootState) => state.document.isLoadingSessions;
export const selectIsLoadingMessages = (state: RootState) => state.document.isLoadingMessages;
export const selectDocumentError = (state: RootState) => state.document.error;
export const selectDocumentPagination = (state: RootState) => state.document.pagination;

export default documentSlice.reducer;